// How the change queue reads a replayed request's answer (BL-2345, ADR 066 — Offline Mode § 4;
// failure classes signed by Michael Lawrie 2026-10-10, plan Q3). Pure: status code and body in,
// the entry's next state out.
import { MOVE_SAME_AUCTION_HINT } from '@auctogon/core/isomorphic/lifecycle';
import { NOTE_ALREADY_DELETED_CODE } from '@auctogon/core-admin/schemas/internal-note';
import { CHANGE_QUEUE_CREATE_OPS, isRecord, type ChangeQueueOp, type JsonObject } from './change-queue-entry';

/** Why an entry failed or was refused, as the queue records it (the store's `OutboxError`). */
export interface ReplayError {
  message: string;
  code: string | null;
  httpStatus: number | null;
  /** The fields a `STALE_FIELDS` refusal named; empty otherwise. */
  fieldNames: string[];
}

/** What one replayed request means for its entry. */
export type ReplayOutcome =
  /** It landed (or had already landed). `serverId` is the record a create made, else null. */
  | { kind: 'synced'; serverId: string | null }
  /** `STALE_FIELDS`: someone else changed a field this edit changes. Discard or Overwrite. */
  | { kind: 'refused'; error: ReplayError }
  /** The server said no for its own reason. Retry or Discard. */
  | { kind: 'failed'; error: ReplayError }
  /** A server error: the entry stays queued and is tried on a later pass. */
  | { kind: 'retry-later'; error: ReplayError }
  /** No answer worth recording (the session expired): stop the pass, leave the entry as it was. */
  | { kind: 'transport'; reason: 'unauthorized' };

/** A server error is retried this many times in all before the entry fails (plan Q3). */
export const MAX_REPLAY_ATTEMPTS = 3;

function stringOf(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function serverIdOf(op: ChangeQueueOp, data: unknown): string | null {
  if (!isRecord(data)) return null;
  if (op === 'note.create') return stringOf(data.id);
  // BL-2448: a relist answers `placed[0]` with the copy it made (its first send and every retry).
  if (op === 'lot.relist') {
    const [placement] = Array.isArray(data.placed) ? data.placed : [];
    return isRecord(placement) ? stringOf(placement.lotId) : null;
  }
  return null;
}

function fieldErrorText(body: JsonObject): string | null {
  if (!isRecord(body.fieldErrors)) return null;
  const parts = Object.values(body.fieldErrors).filter((v): v is string => typeof v === 'string' && v.length > 0);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * A refusal that means "this change is already on the server": a re-sent status change, move or
 * note delete after the app was killed between the server's answer and the queue recording it (AC 2). Creates,
 * relists and per-item media need no rule here: they answer a repeat as a success (BL-2337, BL-2338,
 * BL-2448).
 */
function alreadyApplied(op: ChangeQueueOp, status: number, body: JsonObject): boolean {
  if (op === 'lot.status' && status === 409 && Array.isArray(body.blocked) && body.blocked.length > 0) {
    return body.blocked.every((b) => isRecord(b) && b.reason === 'already-in-phase');
  }
  if (op === 'lot.move') return isRecord(body.fieldErrors) && body.fieldErrors.auctionId === MOVE_SAME_AUCTION_HINT;
  // BUG-2060: only the 404 that names the note as already deleted. The gate's own 404 (a foreign,
  // unknown or retired-type note) means the delete never ran, so it fails with the server's reason.
  return op === 'note.delete' && status === 404 && body.code === NOTE_ALREADY_DELETED_CODE;
}

/** The error a refusal records: the server's field messages or its own words, and its code and status. */
export function replayErrorOf(status: number, body: JsonObject): ReplayError {
  return {
    message: fieldErrorText(body) ?? stringOf(body.error) ?? 'The server could not take this change.',
    code: stringOf(body.code),
    httpStatus: status,
    fieldNames: [],
  };
}

/**
 * Classify one replayed request's answer.
 *
 * @param input.op - the entry's op.
 * @param input.status - the HTTP status.
 * @param input.body - the parsed JSON body, or null when there was none.
 * @returns what the queue does with the entry.
 */
export function classifyReplayResponse(input: { op: ChangeQueueOp; status: number; body: unknown }): ReplayOutcome {
  const { op, status } = input;
  const body = isRecord(input.body) ? input.body : {};
  if (status >= 200 && status < 300) {
    const serverId = serverIdOf(op, body.data);
    // A create that landed but names no record would strand everything waiting on it: keep it
    // queued (its clientOpId answers a retry with the same record, BL-2337).
    if (serverId == null && CHANGE_QUEUE_CREATE_OPS.has(op)) {
      return { kind: 'failed', error: { message: 'The server saved this change but did not say what it created.', code: null, httpStatus: status, fieldNames: [] } };
    }
    return { kind: 'synced', serverId };
  }
  if (status === 401) return { kind: 'transport', reason: 'unauthorized' };
  if (alreadyApplied(op, status, body)) return { kind: 'synced', serverId: null };
  if (status === 409 && body.code === 'STALE_FIELDS') {
    const fieldNames = Array.isArray(body.staleFields) ? body.staleFields.filter((f): f is string => typeof f === 'string') : [];
    return { kind: 'refused', error: { ...replayErrorOf(status, body), fieldNames } };
  }
  if (status >= 500) return { kind: 'retry-later', error: replayErrorOf(status, body) };
  return { kind: 'failed', error: replayErrorOf(status, body) };
}
